import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  Share2, Copy, ExternalLink, CheckCircle2, Loader2,
  Rss, Linkedin, Twitter, Facebook, MessageCircle, X
} from 'lucide-react';

export function JobSyndicationPanel({ jobId, onClose }) {
  const { api } = useAuth();
  const [links, setLinks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    api.get(`/jobs/${jobId}/syndication`)
      .then(r => setLinks(r.data))
      .catch(() => toast.error('Failed to load syndication links'))
      .finally(() => setLoading(false));
  }, [jobId]);

  const copy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(''), 2000);
      toast.success('Copied!');
    } catch { toast.error('Copy failed'); }
  };

  const SHARE_BUTTONS = links ? [
    {
      key: 'linkedin',
      label: 'Share on LinkedIn',
      icon: Linkedin,
      color: 'bg-[#0077B5] hover:bg-[#006097]',
      href: links.linkedin_share,
    },
    {
      key: 'twitter',
      label: 'Share on X / Twitter',
      icon: Twitter,
      color: 'bg-black hover:bg-gray-800',
      href: links.twitter_share,
    },
    {
      key: 'facebook',
      label: 'Share on Facebook',
      icon: Facebook,
      color: 'bg-[#1877F2] hover:bg-[#166FE5]',
      href: links.facebook_share,
    },
    {
      key: 'whatsapp',
      label: 'Share on WhatsApp',
      icon: MessageCircle,
      color: 'bg-[#25D366] hover:bg-[#20BD5A]',
      href: links.whatsapp_share,
    },
  ] : [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-[#003366]" />
            <h3 className="font-bold text-lg">Syndicate Job</h3>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-[#003366]" />
          </div>
        ) : links ? (
          <div className="space-y-5">
            {/* Social share */}
            <div>
              <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Share on Social</p>
              <div className="grid grid-cols-2 gap-2">
                {SHARE_BUTTONS.map(btn => (
                  <a
                    key={btn.key}
                    href={btn.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-2 text-sm text-white font-medium py-2.5 px-4 rounded-xl transition-colors ${btn.color}`}
                  >
                    <btn.icon className="w-4 h-4" />{btn.label.replace('Share on ', '')}
                    <ExternalLink className="w-3 h-3 ml-auto opacity-60" />
                  </a>
                ))}
              </div>
            </div>

            {/* Copy job URL */}
            <div>
              <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">Job URL</p>
              <div className="flex items-center gap-2 border rounded-xl p-3 bg-gray-50">
                <p className="text-sm text-gray-600 flex-1 truncate">{links.job_url}</p>
                <button
                  onClick={() => copy('url', links.job_url)}
                  className="shrink-0 text-[#003366] hover:text-[#003366]/70 transition-colors"
                >
                  {copied === 'url' ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Indeed feed */}
            <div>
              <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                <Rss className="w-3.5 h-3.5 inline mr-1 text-[#003366]" />
                Indeed XML Feed
              </p>
              <p className="text-xs text-muted-foreground mb-2">
                Add this feed URL to your Indeed Employer dashboard to auto-syndicate all active jobs.
              </p>
              <div className="flex items-center gap-2 border rounded-xl p-3 bg-gray-50">
                <p className="text-xs text-gray-600 flex-1 truncate">{links.indeed_feed_url}</p>
                <button
                  onClick={() => copy('indeed', links.indeed_feed_url)}
                  className="shrink-0 text-[#003366] hover:text-[#003366]/70"
                >
                  {copied === 'indeed' ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </button>
                <a href={links.indeed_feed_url} target="_blank" rel="noopener noreferrer" className="shrink-0 text-[#003366] hover:text-[#003366]/70">
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>

            <p className="text-xs text-muted-foreground bg-blue-50 border border-blue-100 rounded-lg p-3">
              <strong>LinkedIn note:</strong> LinkedIn's Jobs API requires a partnership. The LinkedIn button above opens a share-post dialog which lets you post the job link to your company feed in one click.
            </p>
          </div>
        ) : (
          <p className="text-muted-foreground text-sm text-center py-4">Failed to load syndication data.</p>
        )}
      </div>
    </div>
  );
}
